import { ICriticalOrgUpdateItem } from 'src/services/models/critical-organization-updates.service';

export const CRITICAL_NEWS_DOCUMENT_TYPE = 2;

export const CRITICAL_NEWS_LANGUAGES = ['English', 'French'];
export const CRITICAL_NEWS_LANGUAGE_CODES: Record<string, number> = {
    English: 1,
    French: 2,
};

const KEEP_ORDER = 0;
const MOVE_AFTER = 1;
const MOVE_BEFORE = -1;
const DATE_PART_LENGTH = 2;
const MONTH_OFFSET = 1;
const ISO_DATE_TIME = /^(\d{4})-(\d{2})-(\d{2})(?:[T ](\d{2}:\d{2}:\d{2}))?/;
const US_DATE_TIME =
    /^(\d{1,2})\/(\d{1,2})\/(\d{4})(?:[T ](\d{2}:\d{2}:\d{2}))?/;

export type CriticalUpdateSortColumn = 'language' | 'startDate' | 'endDate';
export type CriticalUpdateSortDirection = 'asc' | 'desc';

interface INewsDateParts {
    year: string;
    month: string;
    day: string;
    time?: string;
}

/** Reads `YYYY-MM-DD[THH:MM:SS]` or `MM/DD/YYYY[ HH:MM:SS]` without a Date object. */
const parseNewsDateTime = (value: string): INewsDateParts | null => {
    const iso = ISO_DATE_TIME.exec(value);

    if (iso) {
        return { year: iso[1], month: iso[2], day: iso[3], time: iso[4] };
    }

    const us = US_DATE_TIME.exec(value);

    return us
        ? {
              year: us[3],
              month: us[1].padStart(DATE_PART_LENGTH, '0'),
              day: us[2].padStart(DATE_PART_LENGTH, '0'),
              time: us[4],
          }
        : null;
};

const getNewsTimestamp = (value?: string | null): number | null => {
    const parts = value ? parseNewsDateTime(value) : null;

    if (!parts) {
        return null;
    }

    const [hours = 0, minutes = 0, seconds = 0] = (parts.time ?? '')
        .split(':')
        .map(Number);

    return new Date(
        Number(parts.year),
        Number(parts.month) - MONTH_OFFSET,
        Number(parts.day),
        hours,
        minutes,
        seconds,
    ).getTime();
};

const getSortValue = (
    item: ICriticalOrgUpdateItem,
    column: CriticalUpdateSortColumn,
): number | null => {
    switch (column) {
        case 'language':
            return Number(item.NEWSLANGUAGE) || null;
        case 'startDate':
            return getNewsTimestamp(item.VALIDFROMDATE);
        default:
            return getNewsTimestamp(item.VALIDTHROUGHDATE);
    }
};

/** Stable sort by column; empty values always go last. */
export const sortCriticalUpdates = (
    items: ICriticalOrgUpdateItem[],
    column: CriticalUpdateSortColumn,
    direction: CriticalUpdateSortDirection = 'asc',
): ICriticalOrgUpdateItem[] =>
    [...items].sort((firstItem, secondItem) => {
        const firstValue = getSortValue(firstItem, column);
        const secondValue = getSortValue(secondItem, column);

        if (firstValue === null && secondValue === null) {
            return KEEP_ORDER;
        }

        if (firstValue === null) {
            return MOVE_AFTER;
        }

        if (secondValue === null) {
            return MOVE_BEFORE;
        }

        const difference = firstValue - secondValue;

        return direction === 'asc' ? difference : -difference;
    });

/** API date (`MM/DD/YYYY [HH:MM:SS]` or ISO) to `MM/DD/YYYY [HH:MM:SS]`. */
export const formatNewsDateTime = (value?: string | null): string => {
    if (!value) {
        return '';
    }

    const parts = parseNewsDateTime(value);

    if (!parts) {
        return value;
    }

    const { year, month, day, time } = parts;

    return `${month}/${day}/${year}${time ? ` ${time}` : ''}`;
};

const HOURS_PER_PERIOD = 12;
const MINUTES_STEP = 30;
const STEPS_PER_HOUR = 2;
const PAD_LENGTH = 2;

const pad = (value: number) => `${value}`.padStart(PAD_LENGTH, '0');

// 12-hour clock starting at 00:00, e.g. "00:00 AM" ... "11:30 AM", "00:00 PM" ... "11:30 PM".
const HALF_HOUR_TIME_OPTIONS: string[] = ['AM', 'PM'].flatMap((period) =>
    Array.from(
        { length: HOURS_PER_PERIOD * STEPS_PER_HOUR },
        (_, index) =>
            `${pad(Math.floor(index / STEPS_PER_HOUR))}:${pad(
                (index % STEPS_PER_HOUR) * MINUTES_STEP,
            )} ${period}`,
    ),
);

export const LAST_MINUTE_TIME_OPTION = '11:59 PM';

export const CRITICAL_NEWS_TIME_OPTIONS: string[] = [
    ...HALF_HOUR_TIME_OPTIONS,
    LAST_MINUTE_TIME_OPTION,
];

export const DEFAULT_START_TIME = '00:00:00';
export const DEFAULT_END_TIME = '23:59:59';

/** Converts a time option ("06:30 PM") to 24-hour "HH:MM:SS" ("18:30:00"). */
export const toNewsTime = (
    time: string,
    fallback = DEFAULT_START_TIME,
): string => {
    if (!time) return fallback;
    if (time === LAST_MINUTE_TIME_OPTION) return DEFAULT_END_TIME;
    const [clock, period] = time.split(' ');
    const [hours, minutes] = clock.split(':').map(Number);
    const hours24 = period === 'PM' ? hours + HOURS_PER_PERIOD : hours;

    return `${pad(hours24)}:${pad(minutes)}:00`;
};

/**
 * Formats a picker date ("YYYY-MM-DD") as "MM/DD/YYYY", plus " HH:MM:SS" when a time
 * is given, without a Date object so no time zone conversion can shift the day.
 */
export const toNewsDate = (
    date: string,
    time?: string,
    fallback?: string,
): string => {
    const [year, month, day] = date.split('-');
    const formattedDate = `${month}/${day}/${year}`;

    return time || fallback
        ? `${formattedDate} ${toNewsTime(time ?? '', fallback)}`
        : formattedDate;
};

export const escapeHtml = (value: string) =>
    value
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');

const ELEMENT_NODE = 1;
const TEXT_NODE = 3;

const ALLOWED_TAGS = new Set([
    'p',
    'br',
    'strong',
    'em',
    'u',
    's',
    'h1',
    'h2',
    'h3',
    'ul',
    'ol',
    'li',
    'blockquote',
    'a',
]);

// Editors emit these equivalents; they are stored as the allowed tag.
const TAG_ALIASES: Record<string, string> = {
    b: 'strong',
    i: 'em',
    strike: 's',
    div: 'p',
};

// Dropped together with their content; other unknown tags keep their text.
const DROPPED_TAGS = new Set([
    'script',
    'style',
    'iframe',
    'object',
    'embed',
    'noscript',
    'template',
    'svg',
    'math',
    'textarea',
    'select',
    'button',
    'form',
    'input',
]);

export const ALLOWED_LINK_PROTOCOLS = ['http:', 'https:', 'mailto:'];

/**
 * Returns the normalized URL when it is http, https or mailto; otherwise null.
 */
export const sanitizeLinkUrl = (value: string): string | null => {
    try {
        const url = new URL(value.trim());

        return ALLOWED_LINK_PROTOCOLS.includes(url.protocol) ? url.href : null;
    } catch {
        return null;
    }
};

const BOLD_FONT_WEIGHT = 600;

// Pasted content (Google Docs, Word, web pages) carries formatting as inline styles.
const getStyleFormats = (element: Element) => {
    const style = (element as HTMLElement).style;
    if (!style) {
        return {
            bold: false,
            italic: false,
            underline: false,
            strike: false,
            isNormalWeight: false,
        };
    }

    const weight = style.fontWeight;
    const decoration = `${style.textDecoration} ${style.getPropertyValue(
        'text-decoration-line',
    )}`;

    return {
        bold:
            weight === 'bold' ||
            weight === 'bolder' ||
            Number(weight) >= BOLD_FONT_WEIGHT,
        italic: ['italic', 'oblique'].includes(style.fontStyle),
        underline: decoration.includes('underline'),
        strike: decoration.includes('line-through'),
        isNormalWeight: weight === 'normal' || weight === '400',
    };
};

const applyStyleFormats = (
    tag: string,
    formats: ReturnType<typeof getStyleFormats>,
    content: string,
): string => {
    let result = content;

    if (formats.strike && tag !== 's') result = `<s>${result}</s>`;
    if (formats.underline && tag !== 'u') result = `<u>${result}</u>`;
    if (formats.italic && tag !== 'em') result = `<em>${result}</em>`;
    if (formats.bold && tag !== 'strong') result = `<strong>${result}</strong>`;

    return result;
};

const serializeChildren = (parent: Node): string =>
    Array.from(parent.childNodes).map(serializeNode).join('');

function serializeNode(node: Node): string {
    if (node.nodeType === TEXT_NODE) {
        return escapeHtml(node.textContent ?? '');
    }
    if (node.nodeType !== ELEMENT_NODE) {
        return '';
    }

    const element = node as Element;
    const sourceTag = element.tagName.toLowerCase();
    const tag = TAG_ALIASES[sourceTag] ?? sourceTag;

    if (DROPPED_TAGS.has(tag)) return '';
    if (tag === 'br') return '<br>';

    const formats = getStyleFormats(element);
    const inner = serializeChildren(element);

    // Google Docs wraps all pasted text in <b style="font-weight:normal">.
    if (tag === 'strong' && formats.isNormalWeight) return inner;

    const content = applyStyleFormats(tag, formats, inner);

    if (!ALLOWED_TAGS.has(tag)) return content;
    if (tag === 'a') {
        const href = sanitizeLinkUrl(element.getAttribute('href') ?? '');

        return href
            ? `<a href="${escapeHtml(
                  href,
              )}" target="_blank" rel="noopener noreferrer">${content}</a>`
            : content;
    }

    return `<${tag}>${content}</${tag}>`;
}

/**
 * Rebuilds the HTML from an allowlist so no script, event handler, style or
 * unsafe link can reach the API. Every attribute is dropped except link href.
 */
export const sanitizeNewsHtml = (html: string): string => {
    const { body } = new DOMParser().parseFromString(html ?? '', 'text/html');

    return serializeChildren(body);
};

export const isBodyEmpty = (html: string): boolean => {
    const { body } = new DOMParser().parseFromString(
        sanitizeNewsHtml(html),
        'text/html',
    );

    return !(body.textContent ?? '').trim();
};
